import React from 'react';
import { useLocation } from 'react-router-dom';
import { getToken } from '../../utils/auth';

/**
 * Nav variant where "Orders" points at the separate Shopify page
 * (/pages/order-portal/) instead of an in-app hash route.
 *
 * Crossing Shopify pages is a real document load, so that link must be an
 * <a href>, not a react-router <Link> - a <Link> would only rewrite the hash
 * and leave you on the current page.
 *
 * PortalNav.jsx is the original single-page version, kept for reference.
 *
 * The portal token is customer-scoped, so both sections are always reachable -
 * there's no case where a customer can see one but not the other.
 *
 * Active state is computed per *section*, not per route, so the tab stays lit
 * on detail pages. NavLink's own matching can't express this: `to="/"` needs
 * `end` (otherwise it matches every route), but `end` then drops the highlight
 * on /draft-order/:id.
 */

const QUOTE_PORTAL_PATH = '/pages/quote-portal/';
const ORDER_PORTAL_PATH = '/pages/order-portal/';

/**
 * In-app route to land on once the order portal page loads.
 *
 * App.jsx mounts one shared <Routes> tree on both Shopify pages, so "/" renders
 * DraftOrdersListPage everywhere - the orders list only lives at "/orders".
 * If App is ever split so the order page owns "/", set this to ''.
 */
// Quotes list is the index route ("/"), so no hash is needed. Note there is no
// "/draft-orders" route in App.jsx - only "/", "/draft-order/:id", "/orders"
// and "/order/:id" - so pointing here at "#/draft-orders" would match nothing.
const QUOTE_PORTAL_HASH = '';
const ORDER_PORTAL_HASH = '#/orders';


/**
 * Cross-page URL carrying the auth token.
 *
 * getToken() falls back to sessionStorage, which is scoped per browser *tab* -
 * a same-tab click would keep it, but a middle-click or "open in new tab" would
 * land on a tokenless page and show "No authentication token found". Putting it
 * in the query string avoids that, and matches how the emailed portal links
 * already work.
 */
function orderPortalHref() {
  const { token } = getToken();
  const query = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${ORDER_PORTAL_PATH}${query}${ORDER_PORTAL_HASH}`;
}
function quotePortalHref() {
  const { token } = getToken();
  const query = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${QUOTE_PORTAL_PATH}${query}${QUOTE_PORTAL_HASH}`;
}

/** True for /orders, /orders/, and /order/:id - but not for /order-something-else. */
function isOrdersSection(pathname) {
  return (
    pathname === '/orders' ||
    pathname.startsWith('/orders/') ||
    pathname.startsWith('/order/')
  );
}
// function isQuoteSection(pathname) {
//   return (
//     pathname === '/draft-orders' ||
//     pathname.startsWith('/draft-orders/') ||
//     pathname.startsWith('/draft-order/')
//   );
// }

/** True for the index and any quote detail route. */
function isQuotesSection(pathname) {
  return pathname === '/' || pathname.startsWith('/draft-order/');
}

function PortalNavOrders() {
  const { pathname } = useLocation();

  // On the order-portal page the hash is "/" like anywhere else, so the hash
  // router can't tell us which Shopify page we're on - read the real path.
  const onOrderPortal =
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith(ORDER_PORTAL_PATH);

  const ordersActive = onOrderPortal || isOrdersSection(pathname);
  // Quotes owns everything that isn't the orders section, including unrecognized
  // hashes, since "/" is the index route. Note this can't reuse the original's
  // `isQuotesSection(pathname) || !ordersActive`: on the order-portal page the
  // hash is "/", which isQuotesSection() calls a quote route, so both tabs would
  // light up at once.
  const quotesActive = !ordersActive;

  const linkClass = (active) =>
    `back-button portal-nav__link${active ? ' portal-nav__link--active' : ''}`;

  return (
    <nav className="portal-nav">
      <a
        href={quotePortalHref()}
        className={linkClass(quotesActive)}
        aria-current={quotesActive ? 'page' : undefined}
      >
        Quotes
      </a>
      <a
        href={orderPortalHref()}
        className={linkClass(ordersActive)}
        aria-current={ordersActive ? 'page' : undefined}
      >
        Orders
      </a>
    </nav>
  );
}

export default PortalNavOrders;
